import { API_ENDPOINTS } from "../../enums/apiUrls";
import { logDataToConsole } from "../../utils/customLogger";
import { getBearerToken } from "./bearerToken";

/**
 * Single PDF descriptor accepted by the merge-PDFs backend.
 *
 * - `source` is the PDF payload. The backend expects either a
 *   `file://<base64>` string (raw base64 without the `data:` prefix) or a
 *   reachable URL.
 * - `order` is the 1-based position the PDF should occupy in the merged
 *   output, expressed as a string (the API contract is stringly-typed).
 */
export interface PdfInfo {
  source: string;
  order: string;
}

interface MergePdfsPayload {
  pdf_info: PdfInfo[];
}

/**
 * Result of a successful {@link mergePdfs} call. The body of the HTTP
 * response is the raw merged PDF, returned here as a `Blob` so the caller
 * can wrap it in a `File`, render it inline, upload it, etc.
 */
export interface MergePdfsResponse {
  blob: Blob;
  success: boolean;
}

/**
 * Merges the supplied PDFs (in the order indicated by each `PdfInfo.order`)
 * into a single PDF on the backend and returns the merged binary as a Blob.
 *
 * Behavior:
 *   - Acquires a bearer token via {@link getBearerToken} on every call (the
 *     helper caches/refreshes tokens internally).
 *   - Posts `{ pdf_info: PdfInfo[] }` to {@link API_ENDPOINTS.MERGE_PDFS}.
 *   - Throws on non-2xx responses with the status code and response body
 *     attached to the error message.
 *   - Forwards the supplied `AbortSignal`, so callers can cancel in-flight
 *     merges (e.g. when the user closes the modal mid-merge).
 *
 * @param pdfInfo Ordered list of PDFs to merge.
 * @param signal  Optional abort signal to cancel the request.
 */
export const mergePdfs = async (
  pdfInfo: PdfInfo[],
  signal?: AbortSignal,
): Promise<MergePdfsResponse> => {
  try {
    const token = await getBearerToken();

    const payload: MergePdfsPayload = {
      pdf_info: pdfInfo,
    };

    const response = await fetch(API_ENDPOINTS.MERGE_PDFS, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
      signal,
    });

    if (!response.ok) {
      const errorData = await response.text().catch(() => "");
      throw new Error(
        `Request failed with status ${response.status}: ${errorData || response.statusText}`,
      );
    }

    const blob = await response.blob();

    return {
      blob,
      success: true,
    };
  } catch (error) {
    logDataToConsole("error in mergePdfs", error);
    throw error;
  }
};
